/**
 * Langue de référence. Toutes les autres sont typées à partir d'elle
 * (`satisfies Dictionary`), donc une clé oubliée est une erreur de compilation
 * et non une phrase restée en anglais que personne ne remarque.
 */
export const en = {
  nav: { features: 'Features', pricing: 'Pricing', faq: 'FAQ', account: 'My account', signIn: 'Sign in' },

  hero: {
    title: 'Alt-Tab for your Mac',
    tagline: 'macOS switches apps. AppSwitch makes ⌥Tab switch windows — every window, including the three documents open in the same app.',
    download: 'Download for Mac',
    seePricing: 'See pricing',
    note: 'Free to use · macOS 15 or later · Apple silicon and Intel',
    hint: 'Hold ⌥ and press Tab — release to switch',
  },

  features: {
    heading: 'Built for people who keep twenty windows open',
    sub: 'One press of ⌥Tab shows them all. Let go and you are there.',
    items: [
      {
        title: 'Every window, not every app',
        body: 'The macOS switcher moves between apps. AppSwitch moves between windows — including the three documents open in the same app.',
      },
      {
        title: 'Keyboard first',
        body: '⌥Tab to move forward, ⌥⇧Tab to go back, arrow keys to navigate the grid. Release ⌥ and the selected window comes to the front.',
      },
      {
        title: 'Never steals focus',
        body: 'Your app stays active until you actually pick. The switcher lives in the menu bar, with no Dock icon to get in your way.',
      },
      {
        title: 'Live previews',
        body: 'See the real content of each window instead of a generic app icon. Minimized windows show up right in the switcher.',
      },
    ],
  },

  pricing: {
    heading: 'Buy once. Own it.',
    sub: 'The switcher itself is free and complete — not a trial, not crippled. Pro adds the two features that need a licence.',
    free: 'Free',
    pro: 'Pro',
    ribbon: 'One-time purchase',
    freePrice: '€0',
    once: 'once',
    freeSub: 'Everything you need to stop using ⌘Tab.',
    proSub: 'No subscription. Nothing renews, ever.',
    freeList: [
      'Full window switcher on ⌥Tab',
      'Keyboard and mouse navigation',
      'Grid and list layouts',
      'Menu bar app, no Dock icon',
    ],
    proList: [
      'Everything in Free',
      'Live previews of every window',
      'Minimized windows in the switcher',
      'Two Macs per licence',
    ],
    download: 'Download',
    buy: 'Get AppSwitch Pro',
    buyBusy: 'Opening checkout…',
    freeFoot: 'No account. No card. No expiry.',
  },

  faq: {
    heading: 'Questions',
    items: [
      {
        q: 'Why does AppSwitch need Accessibility access?',
        a: 'It is the only way macOS lets an app capture ⌥Tab before other apps see it, enumerate your open windows and bring one to the front. Without it the app cannot work at all. You grant it yourself in System Settings and can revoke it at any time.',
      },
      {
        q: 'Is this a subscription?',
        a: 'No. AppSwitch Pro is a one-time purchase. Nothing renews, nothing is charged again, and updates to the current major version are included.',
      },
      {
        q: 'How many Macs can I use it on?',
        a: 'Two per licence. You can free a seat at any time from your account area or from within the app, so changing machines is never a support ticket.',
      },
      {
        q: 'Does it send my window titles anywhere?',
        a: 'No. No window content, no window title and no keystroke ever leaves your Mac. The only network request the app makes is a licence check, and only if you bought Pro. The free version never touches the network.',
      },
      {
        q: 'Which macOS versions are supported?',
        a: 'macOS 15 or later, on Apple silicon and Intel.',
      },
    ],
  },

  listMode: {
    heading: 'Two ways to see your windows',
    sub: 'Switch between them from the menu bar, or press , while the switcher is open.',
    grid: { title: 'Grid, with previews', body: 'Thumbnails of the real window content. Best when you recognise a window by what is in it.' },
    list: { title: 'List, with full titles', body: 'A dense single column showing each window’s complete title. Best for twenty terminals, or files whose names only differ at the end.' },
  },

  footer: { terms: 'Terms of Use', privacy: 'Privacy Policy' },

  consent: {
    text: 'We use Google Analytics to understand which pages are useful. Nothing is measured unless you agree. See our',
    privacyLink: 'privacy policy',
    accept: 'Accept',
    decline: 'Decline',
  },
}

/// Pas de `as const` ici : il figerait chaque phrase anglaise en type littéral,
/// et toute traduction serait alors « incompatible » avec la référence. Ce qu'on
/// veut contraindre, c'est la *forme* du dictionnaire, pas son contenu.
export type Dictionary = typeof en
